// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { buildCaseAddressLegendAnalysis, normalizeCaseAddressLegendAddressKey } from './CaseBuddyAddressLegend.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import {
  formatCaseSuspiciousAmount,
  getCaseSuspiciousAddressLegendEntries,
  normalizeCaseSuspiciousHash,
  parseCaseSuspiciousAmount
} from './CaseBuddySuspiciousAddressTable.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_EXCHANGE_ADDRESS_TABLE_HEADERS = Object.freeze(['Address', 'Exchange', 'Label', 'Received Amounts']);

export const CASE_EXCHANGE_ADDRESS_TABLE_NOISE_PATTERNS = Object.freeze([
  /\bunnamed\s+service\b/i,
  /\bunknown[_\s-]*entity\b/i,
  /\bunknown\s+service\b/i,
  /\bunknown\s+exchange\b/i,
  /\bunknown\b/i,
  /\bgeneric\s+service\b/i
]);

export function normalizeCaseExchangeAddressTableHash(value = '') {
  return normalizeCaseSuspiciousHash(value);
}

export function parseCaseExchangeAddressTableAmount(value = '') {
  return parseCaseSuspiciousAmount(value);
}

export function formatCaseExchangeAddressTableAmount(amount = 0, token = '') {
  return formatCaseSuspiciousAmount(amount, token);
}

export function getCaseExchangeAddressTableLegendEntries(legendAnalysis = {}) {
  return getCaseSuspiciousAddressLegendEntries(legendAnalysis);
}

export function cleanCaseExchangeLegendEntityName(value = '') {
  let text = normalizeCell(value);
  if (!text) return '';
  text = text.replace(/\u2019/g, "'");
  text = text.replace(/\s*#\s*\d+\s*$/i, '').trim();
  text = text.replace(/\s+(Deposit\s+Address|Hot\s+Wallet\s+Address|Hot\s+Wallet|Address)$/i, '').trim();
  return text;
}

export function isPotentialCaseExchangeAddressNoise(value = '') {
  const text = normalizeCell(value);
  if (!text) return true;
  return CASE_EXCHANGE_ADDRESS_TABLE_NOISE_PATTERNS.some(pattern => pattern.test(text));
}

export function buildCaseExchangeAddressLegendLookup(legendAnalysis = {}) {
  const lookup = new Map();
  getCaseExchangeAddressTableLegendEntries(legendAnalysis).forEach((entry) => {
    const address = normalizeCell(entry?.address || entry?.Address || entry?.preferredDisplayAddress || entry?.PreferredDisplayAddress);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (!key || lookup.has(key)) return;
    const rawFamily = normalizeCell(entry?.labelFamily || entry?.family || entry?.LabelFamily || entry?.label_family);
    const label = normalizeCell(entry?.label || entry?.Label || entry?.proposedLegendLabel || entry?.ProposedLegendLabel);
    const looksExchangeFamily = /^exchange(?:_|$)/i.test(rawFamily);
    if (!looksExchangeFamily) return;
    const exchangeName = cleanCaseExchangeLegendEntityName(
      entry?.entityPresentedName ||
      entry?.entity_presented_name ||
      entry?.exchangeName ||
      entry?.ExchangeName ||
      label
    );
    if (!exchangeName) return;
    lookup.set(key, {
      normalizedAddress: key,
      address,
      label,
      exchangeName,
      labelFamily: rawFamily,
      isPotentialNoise: isPotentialCaseExchangeAddressNoise(exchangeName) || isPotentialCaseExchangeAddressNoise(label)
    });
  });
  return lookup;
}

export function mergeCaseExchangeUniqueMultiline(existingText = '', newText = '') {
  const values = normalizeCell(existingText)
    .split('\n')
    .map(value => normalizeCell(value))
    .filter(Boolean);
  const incoming = normalizeCell(newText);
  if (incoming && !values.some(value => value.toLowerCase() === incoming.toLowerCase())) values.push(incoming);
  return values.join('\n');
}

export function buildCaseExchangeReceivedSummaries(tokens = new Map()) {
  const displayLines = [];
  const cellLines = [];
  Array.from(tokens.entries())
    .sort((left, right) => normalizeCell(left[0]).localeCompare(normalizeCell(right[0]), undefined, { sensitivity: 'base' }))
    .forEach(([token, amounts]) => {
      const sortedAmounts = (Array.isArray(amounts) ? amounts : []).slice().sort((left, right) => left - right);
      sortedAmounts.forEach((amount) => {
        const line = `${formatCaseExchangeAddressTableAmount(amount, token)} ${normalizeCell(token)}`.trim();
        displayLines.push(line);
        cellLines.push(line);
      });
    });
  return {
    display: displayLines.join('\\n'),
    cell: cellLines.join('\n'),
    csv: cellLines.join('\n')
  };
}

export function getCaseExchangeAddressSortParts(label = '') {
  const text = normalizeCell(label);
  const base = cleanCaseExchangeLegendEntityName(text.replace(/\s*#\s*\d+\s*$/i, '').trim()) || text;
  const numberMatch = text.match(/#\s*(\d+)\s*$/);
  return {
    base,
    number: numberMatch ? Number.parseInt(numberMatch[1], 10) : Number.MAX_SAFE_INTEGER
  };
}

export function buildCaseExchangeAddressTableRowsFromGroups(groups = new Map(), exclusionsByAddressKey = {}) {
  return Array.from(groups.values()).map((group) => {
    const summaries = buildCaseExchangeReceivedSummaries(group.tokens);
    const uniqueTransactionCount = group.transactionHashes instanceof Set ? group.transactionHashes.size : 0;
    const exclusion = exclusionsByAddressKey?.[group.normalizedAddress] || null;
    const isExcludedCurrent = !!exclusion?.excludedAtUtc;
    const potentialNoise = !!group.isPotentialNoise || isPotentialCaseExchangeAddressNoise(group.exchangeName) || isPotentialCaseExchangeAddressNoise(group.label);
    return {
      key: `exchange-address-${caseBuddyHashString(`${group.normalizedAddress}|${group.exchangeName}|${group.label}`)}`,
      reviewKey: group.normalizedAddress,
      normalizedAddress: group.normalizedAddress,
      address: normalizeCell(group.address),
      exchangeName: normalizeCell(group.exchangeName),
      label: normalizeCell(group.label),
      receivedSummaryDisplay: summaries.display,
      receivedSummaryCell: summaries.cell,
      receivedSummaryCsv: summaries.csv,
      sortNumber: group.sortNumber,
      sortBase: group.sortBase,
      uniqueTransactionCount,
      contributionCount: Number(group.contributionCount) || 0,
      sourceRows: Array.isArray(group.sourceRows) ? group.sourceRows.slice() : [],
      firstRowNumber: group.sourceRows?.[0]?.rowNumber || '',
      isExcludedCurrent,
      exclusionComment: normalizeCell(exclusion?.comment || ''),
      excludedAtUtc: normalizeCell(exclusion?.excludedAtUtc || ''),
      isPotentialNoise: potentialNoise,
      statusLabel: isExcludedCurrent ? 'Excluded' : potentialNoise ? 'Review' : 'Included',
      searchText: [group.address, group.exchangeName, group.label, summaries.display, isExcludedCurrent ? 'excluded' : 'included', potentialNoise ? 'potential noise' : '', ...(group.sourceRows || []).flatMap(row => [row.rowNumber, row.pathLabel, row.transactionHash, row.amount, row.token, row.notes, row.attribution])]
        .map(value => normalizeCell(value).toLowerCase())
        .filter(Boolean)
        .join(' ')
    };
  }).sort((left, right) => {
    if (left.isExcludedCurrent !== right.isExcludedCurrent) return left.isExcludedCurrent ? 1 : -1;
    const exchangeCompare = normalizeCell(left.exchangeName).localeCompare(normalizeCell(right.exchangeName), undefined, { numeric: true, sensitivity: 'base' });
    if (exchangeCompare !== 0) return exchangeCompare;
    const baseCompare = normalizeCell(left.sortBase || left.label).localeCompare(normalizeCell(right.sortBase || right.label), undefined, { numeric: true, sensitivity: 'base' });
    if (baseCompare !== 0) return baseCompare;
    if (left.sortNumber !== right.sortNumber) return left.sortNumber - right.sortNumber;
    return normalizeCell(left.address).localeCompare(normalizeCell(right.address), undefined, { numeric: true, sensitivity: 'base' });
  });
}

export function buildCaseExchangeAddressTableSummary(state = {}) {
  if (!state.legendBuildIsCurrent) return 'Exchange address table needs the Address Legend to be built from current Main Data first.';
  if (!state.legendReviewIsCurrent) return 'Exchange address table needs the Address Legend to be reviewed for the current case data first.';
  const total = Number(state.totalCandidateCount) || 0;
  const visible = Number(state.visibleRowCount) || 0;
  const excluded = Number(state.excludedCurrentCount) || 0;
  const contributions = Number(state.contributionCount) || 0;
  const txCount = Number(state.uniqueTransactionCount) || 0;
  if (total === 0) return `Exchange address table is buildable, but no reviewed Address Legend exchange/service receiving labels matched current Main Data. ${txCount} unique transaction${txCount === 1 ? '' : 's'} checked.`;
  let summary = `Exchange address table is buildable with ${visible} included row${visible === 1 ? '' : 's'} from ${contributions} deduped transfer contribution${contributions === 1 ? '' : 's'} across ${txCount} unique transaction${txCount === 1 ? '' : 's'} checked.`;
  if (excluded > 0) summary += ` ${excluded} candidate row${excluded === 1 ? '' : 's'} excluded from this case's final exchange table.`;
  if ((state.potentialNoiseCount || 0) > 0) summary += ` ${state.potentialNoiseCount} candidate row${state.potentialNoiseCount === 1 ? '' : 's'} look like possible noisy exchange/entity labels and should be reviewed.`;
  return summary;
}

export function buildCaseExchangeAddressTableState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(
    rows,
    effectiveCurator,
    workflowState,
    { labelDormantAddressesSeparately: !!options.labelDormantAddressesSeparately }
  );
  const legendLookup = buildCaseExchangeAddressLegendLookup(legendAnalysis);
  const entries = parseCaseEntries(rows);
  const reviewState = workflowState?.exchangeAddressTableReview && typeof workflowState.exchangeAddressTableReview === 'object'
    ? workflowState.exchangeAddressTableReview
    : {};
  const exclusionsByAddressKey = reviewState.exclusionsByAddressKey && typeof reviewState.exclusionsByAddressKey === 'object'
    ? reviewState.exclusionsByAddressKey
    : {};
  const groups = new Map();
  const seenContributionKeys = new Set();
  const uniqueTransactionHashes = new Set();
  const state = {
    caseId: normalizeCell(options.caseId),
    legendBuildIsCurrent: !!legendAnalysis.buildIsCurrent,
    legendReviewIsCurrent: !!legendAnalysis.reviewIsCurrent,
    tableIsCurrent: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    canBuild: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    legendEntryCount: legendLookup.size,
    uniqueTransactionCount: 0,
    totalCandidateCount: 0,
    visibleRowCount: 0,
    excludedCurrentCount: 0,
    contributionCount: 0,
    sourceRowCount: 0,
    skippedMissingAmountCount: 0,
    skippedMissingTokenCount: 0,
    potentialNoiseCount: 0,
    potentialNoiseExamples: [],
    rows: [],
    sourceRows: [],
    summary: '',
    currentBasisHash: '',
    legendAnalysis
  };

  entries.forEach((entry) => {
    const entryHash = normalizeCaseExchangeAddressTableHash(entry?.hash || '');
    if (entryHash) uniqueTransactionHashes.add(entryHash);

    (entry?.rows || []).forEach((entryRow) => {
      const row = Array.isArray(entryRow?.row) ? entryRow.row : [];
      if (!rowHasMeaningfulData(row)) return;
      const toAddress = normalizeCell(row[3]);
      if (!toAddress) return;
      const addressKey = normalizeCaseAddressLegendAddressKey(toAddress);
      if (!addressKey) return;
      const legend = legendLookup.get(addressKey);
      if (!legend?.exchangeName) return;
      const amount = parseCaseExchangeAddressTableAmount(row[5]);
      if (amount == null) {
        state.skippedMissingAmountCount += 1;
        return;
      }
      const token = normalizeCell(row[6]);
      if (!token) {
        state.skippedMissingTokenCount += 1;
        return;
      }
      const transactionHash = normalizeCaseExchangeAddressTableHash(row[1]) || entryHash || `row-${entryRow.rowIndex}`;
      const contributionKey = [transactionHash, addressKey, String(amount), token.toUpperCase()].join('|');
      if (seenContributionKeys.has(contributionKey)) return;
      seenContributionKeys.add(contributionKey);

      if (!groups.has(addressKey)) {
        const sortParts = getCaseExchangeAddressSortParts(legend.label);
        groups.set(addressKey, {
          normalizedAddress: addressKey,
          address: toAddress,
          exchangeName: legend.exchangeName,
          label: legend.label,
          sortBase: sortParts.base,
          sortNumber: sortParts.number,
          isPotentialNoise: !!legend.isPotentialNoise,
          tokens: new Map(),
          transactionHashes: new Set(),
          sourceRows: [],
          contributionCount: 0
        });
      }
      const group = groups.get(addressKey);
      group.exchangeName = mergeCaseExchangeUniqueMultiline(group.exchangeName, legend.exchangeName);
      group.label = mergeCaseExchangeUniqueMultiline(group.label, legend.label);
      group.isPotentialNoise = group.isPotentialNoise || !!legend.isPotentialNoise;
      if (!group.tokens.has(token)) group.tokens.set(token, []);
      group.tokens.get(token).push(amount);
      group.transactionHashes.add(transactionHash);
      group.contributionCount += 1;
      const source = {
        rowNumber: Number(entryRow.rowIndex) + 1,
        rowIndex: Number(entryRow.rowIndex),
        pathLabel: normalizeCell(row[0]) || normalizeCell(entry.rows?.[0]?.row?.[0]),
        transactionHash,
        address: toAddress,
        amount,
        token,
        formattedAmount: `${formatCaseExchangeAddressTableAmount(amount, token)} ${token}`.trim(),
        notes: normalizeCell(row[9]),
        attribution: normalizeCell(row[10])
      };
      group.sourceRows.push(source);
      state.sourceRows.push(source);
    });
  });

  state.rows = buildCaseExchangeAddressTableRowsFromGroups(groups, exclusionsByAddressKey);
  state.totalCandidateCount = state.rows.length;
  state.visibleRowCount = state.rows.filter(row => !row.isExcludedCurrent).length;
  state.excludedCurrentCount = state.rows.filter(row => row.isExcludedCurrent).length;
  state.potentialNoiseCount = state.rows.filter(row => row.isPotentialNoise && !row.isExcludedCurrent).length;
  state.potentialNoiseExamples = state.rows
    .filter(row => row.isPotentialNoise)
    .map(row => row.exchangeName || row.label)
    .filter((value, index, array) => value && array.findIndex(other => normalizeCell(other).toLowerCase() === normalizeCell(value).toLowerCase()) === index);
  state.contributionCount = seenContributionKeys.size;
  state.sourceRowCount = state.sourceRows.length;
  state.uniqueTransactionCount = uniqueTransactionHashes.size;
  state.summary = buildCaseExchangeAddressTableSummary(state);
  state.currentBasisHash = `exchange-address-table-${caseBuddyHashString(JSON.stringify({
    legendBasisHash: legendAnalysis.currentBasisHash || '',
    legendReviewedBasisHash: legendAnalysis.confirmedReviewBasisHash || '',
    rows: state.rows.map(row => [row.normalizedAddress, row.address, row.exchangeName, row.label, row.receivedSummaryDisplay, row.uniqueTransactionCount, row.contributionCount, row.isExcludedCurrent]),
    skippedMissingAmountCount: state.skippedMissingAmountCount,
    skippedMissingTokenCount: state.skippedMissingTokenCount
  }))}`;
  return state;
}

export function buildCaseExchangeAddressTableDeliverableRows(state = {}) {
  const rows = [CASE_EXCHANGE_ADDRESS_TABLE_HEADERS.slice()];
  (Array.isArray(state.rows) ? state.rows : []).forEach((row) => {
    if (row?.isExcludedCurrent) return;
    rows.push([
      normalizeCell(row.address),
      normalizeCell(row.exchangeName),
      normalizeCell(row.label),
      normalizeCell(row.receivedSummaryCell || row.receivedSummaryDisplay)
    ]);
  });
  return rows;
}

export function buildCaseExchangeAddressTableAuditRows(state = {}, actionLabel = 'Exchange address table review') {
  return [
    ['Case Buddy Exchange Address Table Audit', '', '', '', '', '', '', '', '', 'Audit only. The exchange address table is generated from Main Data plus the reviewed Address Legend; Main Data is not modified.'],
    ['Action', actionLabel, 'Included Rows', state.visibleRowCount || 0, 'Excluded Rows', state.excludedCurrentCount || 0, 'Deduped Contributions', state.contributionCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Legend Current', state.legendBuildIsCurrent ? 'Yes' : 'No', 'Legend Reviewed', state.legendReviewIsCurrent ? 'Yes' : 'No', 'Potential Noise Rows', state.potentialNoiseCount || 0],
    [],
    ...buildCaseExchangeAddressTableDeliverableRows(state),
    [],
    ['Excluded Rows'],
    ['Address', 'Exchange', 'Label', 'Reason'],
    ...(Array.isArray(state.rows) ? state.rows : []).filter(row => row.isExcludedCurrent).map(row => [
      row.address || '',
      row.exchangeName || '',
      row.label || '',
      row.exclusionComment || ''
    ]),
    [],
    ['Source Rows'],
    ['Row', 'Path Label', 'Transaction Hash', 'Address', 'Amount', 'Token', 'Attribution', 'Notes'],
    ...(Array.isArray(state.sourceRows) ? state.sourceRows : []).map(row => [
      row.rowNumber || '',
      row.pathLabel || '',
      row.transactionHash || '',
      row.address || '',
      row.formattedAmount || '',
      row.token || '',
      row.attribution || '',
      row.notes || ''
    ])
  ];
}
